// /reports/expense-analysis — Expense Analysis (PAGES.md §3.93)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { SalesWaveChart } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { ExpenseCategoryRow, ExpenseStaffRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type CatRow = ExpenseCategoryRow & { id: string };
type StaffRow = ExpenseStaffRow & { id: string };

function makeCatCols(currency: CurrencyCode): ColumnDef<CatRow>[] {
  return [
    { key: 'category', header: 'Category', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.category}</span> },
    { key: 'count', header: 'Count', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.count.toLocaleString('en-IN')}</span> },
    { key: 'total', header: 'Total', numeric: true, render: (_v, row) => <span className="text-sm font-semibold text-warning-700 dark:text-warning-300 tabular-nums">{formatPaise(row.total, currency)}</span> },
    { key: 'avg', header: 'Avg', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.avg, currency)}</span> },
    { key: 'pct_of_total', header: 'Share', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.pct_of_total.toFixed(1)}%</span> },
  ];
}

function makeStaffCols(currency: CurrencyCode): ColumnDef<StaffRow>[] {
  return [
    { key: 'name', header: 'Staff', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</span> },
    { key: 'submissions', header: 'Submitted', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.submissions}</span> },
    { key: 'approved', header: 'Approved', numeric: true, render: (_v, row) => <span className="text-success-600 tabular-nums">{row.approved}</span> },
    { key: 'rejected', header: 'Rejected', numeric: true, render: (_v, row) => <span className="text-danger-600 tabular-nums">{row.rejected}</span> },
    { key: 'total_amount', header: 'Total', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total_amount, currency)}</span> },
  ];
}

export function ExpenseAnalysisReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const catCols = useMemo(() => makeCatCols(currency), [currency]);
  const staffCols = useMemo(() => makeStaffCols(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'expense-analysis',
    filter,
    queryFn: (f) => reportsApi.getExpenseAnalysis(f),
  });

  const cats: CatRow[] = useMemo(
    () => (data?.categories ?? []).map((c, i) => ({ ...c, id: `${c.category}-${i}` })),
    [data],
  );
  const staff: StaffRow[] = useMemo(
    () => (data?.staff ?? []).map((s) => ({ ...s, id: s.user_id })),
    [data],
  );

  const trendData = (data?.trend ?? []).map((t) => ({ label: t.date.slice(5), today: t.amount / subunit, yesterday: 0 }));

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `expense-analysis-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Category', get: (r: CatRow) => r.category },
          { header: 'Count', get: (r: CatRow) => r.count },
          { header: `Total (${symbol})`, get: (r: CatRow) => (r.total / subunit).toFixed(csvDecimals) },
          { header: `Avg (${symbol})`, get: (r: CatRow) => (r.avg / subunit).toFixed(csvDecimals) },
          { header: 'Share %', get: (r: CatRow) => r.pct_of_total.toFixed(1) },
        ],
        cats,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Expense Analysis', `Total: ${formatPaise(data?.total_expenses ?? 0, currency)} • Approved: ${formatPaise(data?.approved_amount ?? 0, currency)}`);
    }
  };

  return (
    <ReportPageShell
      title="Expense Analysis"
      subtitle="Category, staff and approval-delay insights with spending trend."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Expense Analysis' }]}
      accent="warning-danger"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!cats.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'total', title: 'Total Expenses', value: formatPaise(data?.total_expenses ?? 0, currency), tone: 'warning', icon: KpiIcons.rupee },
              { id: 'approved', title: 'Approved', value: formatPaise(data?.approved_amount ?? 0, currency), tone: 'success', icon: KpiIcons.trendUp },
              { id: 'rejected', title: 'Rejected', value: formatPaise(data?.rejected_amount ?? 0, currency), tone: 'danger', icon: KpiIcons.trendDown },
              { id: 'delay', title: 'Avg Approval Delay', value: `${(data?.avg_approval_delay_hours ?? 0).toFixed(1)}h`, tone: 'indigo', icon: KpiIcons.clock },
            ]}
          />
          {trendData.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <SalesWaveChart data={trendData} currency={currency} title={`Daily Expense Trend (${symbol})`} />
            </div>
          ) : null}
          <ReportTable title="By Category" columns={catCols} data={cats} emptyTitle="No expense data for selected period" totalCount={cats.length} />
          <ReportTable title="By Staff" columns={staffCols} data={staff} emptyTitle="No staff expense data" totalCount={staff.length} />

          {/* Approval Delay Summary — §3.93 requires approval delay insights */}
          <section className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up mb-6">
            <header className="border-b border-neutral-100 dark:border-neutral-700/60 px-5 py-4 bg-gradient-to-r from-neutral-50/70 to-transparent dark:from-neutral-800/80">
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-warning-500 to-danger-500" />
                Approval Delay Analysis
              </h2>
            </header>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-warning-50 dark:bg-warning-900/20 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-warning-600 dark:text-warning-300 mb-1">Avg Time to Approve</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{(data?.avg_approval_delay_hours ?? 0).toFixed(1)}h</p>
              </div>
              <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-danger-600 dark:text-danger-300 mb-1">Longest Pending</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{(data?.longest_pending_hours ?? 0).toFixed(1)}h</p>
              </div>
            </div>
          </section>
        </>
      )}
    </ReportPageShell>
  );
}
